import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { removeEverythingButNumericValues } from "~/utils/formatNumbers";
import { cn } from "./utils/cn";
import Label from "./Label";
import FloatingLabel from "./FloatingLabel";
import FieldError from "./FieldError";

type NativeProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "size" | "type"
>;

export interface NumericFieldProps extends NativeProps {
  value?: string | number;
  onChange: (value: string) => void;
  label?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  /** Suffix unit (e.g. ₪, %). */
  sign?: ReactNode;
  /** Max digits after the decimal point. Defaults to 2. */
  maxFractionDigits?: number;
  /** `default` — label above; `compact` — label on the top border. */
  variant?: "default" | "compact";
  containerClassName?: string;
}

/**
 * Numeric input that sanitizes to digits + a single decimal and forces
 * `dir="ltr"` so numerals stay left-to-right inside RTL forms. Supersedes
 * common/NumericInput.
 */
const NumericField = forwardRef<HTMLInputElement, NumericFieldProps>(
  (
    {
      value,
      onChange,
      label,
      required,
      disabled,
      error,
      sign,
      maxFractionDigits = 2,
      variant = "default",
      id,
      className,
      containerClassName,
      ...rest
    },
    ref
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const errorId = error ? `${inputId}-error` : undefined;
    const compact = variant === "compact";

    const handleChange = (raw: string) => {
      const formatted = removeEverythingButNumericValues(raw);
      const parts = formatted.split(".");
      const next =
        parts.length >= 2
          ? `${parts[0]}.${parts[1].slice(0, maxFractionDigits)}`
          : formatted;
      onChange(next);
    };

    const input = (
      <input
        ref={ref}
        id={inputId}
        dir="ltr"
        inputMode="decimal"
        value={value ?? ""}
        disabled={disabled}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        aria-label={compact && typeof label === "string" ? label : undefined}
        onChange={(event) => handleChange(event.target.value)}
        className={cn(
          "w-full bg-transparent text-end text-menu focus:outline-none disabled:cursor-not-allowed",
          className
        )}
        {...rest}
      />
    );

    if (compact) {
      return (
        <div>
          <div
            className={cn(
              "transition-colors group relative flex h-control items-center gap-3 rounded-control border bg-surface px-3",
              error
                ? "border-danger"
                : "border-border-subtle focus-within:border-secondary",
              disabled && "opacity-60",
              containerClassName
            )}
          >
            {label && (
              <FloatingLabel
                htmlFor={inputId}
                required={required}
                error={Boolean(error)}
              >
                {label}
              </FloatingLabel>
            )}
            {input}
            {sign && <span className="shrink-0 text-menu/70">{sign}</span>}
          </div>
          <FieldError id={errorId}>{error}</FieldError>
        </div>
      );
    }

    return (
      <div>
        {label && (
          <Label htmlFor={inputId} required={required} readOnly={disabled}>
            {label}
          </Label>
        )}
        <div
          className={cn(
            "transition flex h-control items-center gap-3 rounded-control border bg-surface px-3",
            error
              ? "border-danger focus-within:border-danger"
              : "border-border-subtle focus-within:border-secondary",
            "focus-within:ring-2 focus-within:ring-secondary/15 focus-within:ring-offset-0",
            disabled && "opacity-60",
            containerClassName
          )}
        >
          {input}
          {sign && <span className="shrink-0 text-menu/70">{sign}</span>}
        </div>
        <FieldError id={errorId}>{error}</FieldError>
      </div>
    );
  }
);

NumericField.displayName = "NumericField";

export default NumericField;
